import { useState } from "react";
import { ProjectRecord } from "@/types";
import { useAuthStore } from "@/store/authStore";
import { useProjectsStore } from "@/store/projectsStore";
import {
  getEnrollmentStatusChangeAllowed,
  getEnrollmentStatusItems,
} from "@/utils/contractorEnrollmentStatusHelpers";
import { PROJECTS_FILTER_ITEMS } from "@/constants";
import { updateEnrolmentStatusUtil } from "@/utils/projectStatusUtils";

export const useProjectStatusUpdate = (projectDetails: ProjectRecord) => {
  const role = useAuthStore((state) => state.role);
  const getProjectDetails = useProjectsStore(
    (state) => state.getProjectDetails
  );

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [rejectProjectId, setRejectProjectId] = useState(0);
  const [correctionNeededProjectId, setCorrectionNeededProjectId] = useState(0);

  const isAdmin = role === "administrator";
  const isContractor = role === "contractor";

  // Function to refresh project details after status update
  const refreshProjectDetails = async () => {
    if (projectDetails?.id) {
      await getProjectDetails(projectDetails.id);
    }
  };

  // Function to update enrollment status (uses shared utility)
  const updateEnrolmentStatus = async (
    projectId: ProjectRecord["id"],
    enrolmentStatus: string
  ) => {
    await updateEnrolmentStatusUtil({
      projectId,
      enrolmentStatus,
      isContractor,
      onReject: setRejectProjectId,
      onCorrectionNeeded: setCorrectionNeededProjectId,
      onSuccess: (async () => {
        // Refresh project details to get latest data
        if (projectDetails?.id) {
          await getProjectDetails(projectDetails.id);
        }
      }) as () => void | Promise<void>,
      onError: setErrorMessage,
    });
  };

  // Determine which enrollment items to show based on role (matches ProjectsTable)
  const enrollmentItems = isContractor
    ? getEnrollmentStatusItems(projectDetails?.enrolmentStatus)
    : PROJECTS_FILTER_ITEMS;

  // Determine if enrollment status is editable
  const isEnrollmentReadOnly = isContractor
    ? !getEnrollmentStatusChangeAllowed(projectDetails?.enrolmentStatus)
    : !isAdmin;

  return {
    // State
    errorMessage,
    rejectProjectId,
    correctionNeededProjectId,

    // Functions
    updateEnrolmentStatus,
    refreshProjectDetails,
    setRejectProjectId,
    setCorrectionNeededProjectId,

    // Computed values
    enrollmentItems,
    isEnrollmentReadOnly,
    isAdmin,
    isContractor,
  };
};
